import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ResourceCenter from './ResourceCenter';
import DistributorReports from './DistributorReports';
import distributorService from '../services/distributorService';
import AdminDistributorCustomers from '../components/distributor/AdminDistributorCustomers';
import { DistributorActivity } from '../components/distributor/DistributorActivity';
import useShippingRequest from '../hooks/useShippingRequest';
import { useT } from '../i18n/LanguageContext';
import '../styles/distributor-admin.css';
import AdminDistributorRecords from './AdminDistributorRecords';

const TITLES = {
  customers: 'Distributor Customers',
  orders: 'Distributor Orders',
  ivrs: 'Distributor IVRs',
  shipments: 'Distributor Shipments',
  resources: 'Distributor Resources & Tools',
  reports: 'Distributor Reports',
};

// Scope stays in the URL for refresh/history. There is deliberately no implicit all-tenant view.
export default function AdminDistributorActivity({ kind }) {
  return ['customers', 'orders', 'ivrs', 'shipments'].includes(kind) ? (
    <AdminDistributorRecords kind={kind} />
  ) : (
    <ScopedDistributorActivity kind={kind} />
  );
}

function ScopedDistributorActivity({ kind }) {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const ids = params.getAll('distributorId');
  const id = ids.length === 1 && /^[a-f\d]{24}$/.test(ids[0]) ? ids[0] : '';
  const invalid = ids.length > 0 && !id;
  const { begin, version } = useShippingRequest('admin-distributor-activity-options');
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  const [locked, setLocked] = useState(false);
  const rows = options?.version === version ? options.rows : null;
  const selected = rows?.find((row) => row._id === id);
  const load = useCallback(async () => {
    const request = begin();
    setOptions(null);
    setError('');
    try {
      const data = (await distributorService.adminDistributors())?.data?.data;
      if (
        !Array.isArray(data) ||
        data.some((row) => !/^[a-f\d]{24}$/.test(row._id) || typeof row.name !== 'string')
      )
        throw new Error('Invalid distributors');
      if (request.isCurrent()) setOptions({ rows: data, version });
    } catch {
      if (request.isCurrent()) setError('Could not load distributors. Please try again.');
    }
  }, [begin, version]);
  useEffect(() => {
    setLocked(false);
    load();
  }, [load]);
  useEffect(() => setLocked(false), [id, kind]);
  return (
    <section
      className="me-distributor-admin mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8"
      aria-label={t(TITLES[kind])}
    >
      <header className="me-distributor-admin-header">
        <p className="me-distributor-admin-eyebrow">{t('Distributor operations')}</p>
        <h1 className="text-3xl font-semibold text-slate-900">{t(TITLES[kind])}</h1>
        <p className="mt-2 text-sm text-slate-600">
          {t('Choose a distributor to view its records. Direct MedEffects records are separate.')}
        </p>
      </header>
      {error && (
        <p role="alert">
          {t(error)}{' '}
          <button type="button" className="underline" onClick={load}>
            {t('Retry')}
          </button>
        </p>
      )}
      {!rows && !error && <p role="status">{t('Loading distributors...')}</p>}
      {rows && (
        <>
          <label className="me-distributor-admin-selector block">
            {t('Distributor')}
            <select
              className="mt-2 block w-full max-w-md rounded border p-2"
              value={selected ? id : ''}
              disabled={locked}
              onChange={(event) =>
                setParams(event.target.value ? { distributorId: event.target.value } : {})
              }
            >
              <option value="">{t('Choose a distributor')}</option>
              {rows.map((row) => (
                <option key={row._id} value={row._id}>
                  {row.name}
                  {row.isActive === false ? ` (${t('Inactive')})` : ''}
                </option>
              ))}
            </select>
          </label>
          {locked && (
            <p role="status">
              {t('Save or discard your current edits before changing distributors.')}
            </p>
          )}
          {(invalid || (id && !selected)) && (
            <p role="alert">
              {t('This distributor is unavailable. Choose a distributor from the list.')}
            </p>
          )}
          {!ids.length && !rows.length && <p>{t('No distributors are available.')}</p>}
          {selected && (
            <section key={`${version}:${id}:${kind}`} aria-label={t('Selected distributor')}>
              <h2 className="mb-4 text-lg font-semibold">{selected.name}</h2>
              {kind === 'reports' ? (
                <DistributorReports adminDistributorId={id} />
              ) : kind === 'resources' ? (
                <ResourceCenter adminDistributorId={id} onLockChange={setLocked} />
              ) : kind === 'customers' ? (
                <AdminDistributorCustomers
                  distributor={selected}
                  onLockChange={setLocked}
                  t={t}
                  initiallyExpanded
                />
              ) : (
                <DistributorActivity t={t} activityKind={kind} adminDistributorId={id} />
              )}
            </section>
          )}
        </>
      )}
    </section>
  );
}
